import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useObOnboardingsApi from './config';
import { isEarlyClose } from './earlyClose';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center; justify-content: center; z-index: 1300; padding: 24px;
`;
const ModalCard = styled.div`
  background: #fff; border-radius: 8px; max-width: 480px; width: 100%; padding: 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
`;
const Field = styled.div` display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; font-size: 14px; `;
const FieldLabel = styled.label` color: #64748b; font-weight: 500; `;
const FieldInput = styled.input` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; `;
const FieldText = styled.textarea` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; resize: vertical; min-height: 60px; font-family: inherit; `;
const Actions = styled.div` display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; `;
// #72 — yerel Primary/Secondary (kopyalanmis blok — onboarding-*/ats-*
// sayfalarinda ayni tarif) kaldirildi, paylasilan Button'a baglandi
// (bkz. t6-report.md).

// Status → set of period(s) for which an approval can be requested.
//
// OBB-07 — UZATILMIŞ SÜREÇ DE İMZA İSTEYEBİLİR. Eski kural yalnız `active`
// diyordu; uzatmadan sonra durum `extended` (dönem `extension`) olduğu için
// "Onayı İste" aksiyonu KAYBOLUYOR, süreç de 90. gün imzası hiç alınmadan
// kapanabiliyordu. Uzatma imzayı ERTELER, kaldırmaz: uzatılmış süreçte istenen
// onay her zaman 90. gün onayıdır (backend de aynı kuralı uygular —
// ObPeriodApprovalDataAccess.RequestApproval, isExtendedDay90).
export const REQUEST_APPROVAL_PERIOD = (status, currentPeriod) => {
  if (status === 'extended') return 'day_61_90';
  if (status !== 'active') return null;
  return currentPeriod;
};

export default function ObOnboardingsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [extendDialog, setExtendDialog] = useState({ isOpen: false, id: null, person: '', days: '', reason: '' });
  const [cancelDialog, setCancelDialog] = useState({ isOpen: false, id: null, person: '', reason: '' });
  const [closeDialog, setCloseDialog] = useState({ isOpen: false, id: null, person: '', notes: '', reason: '', expectedDay90: null });
  const [requestDialog, setRequestDialog] = useState({ isOpen: false, id: null, person: '', period: '', note: '' });
  const [startDateDialog, setStartDateDialog] = useState({ isOpen: false, formData: null });
  const [busy, setBusy] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, objectSchema, extend, cancel, close, create, requestApproval } = useObOnboardingsApi(queryParams);

  const handleNewButtonClick = useCallback(() => setDrawerOpen(true), []);

  const doCreate = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [create]);

  const handleSave = useCallback(async (formData) => {
    // #3 soft warning: if the chosen start date differs from the employee's actual
    // employment date, confirm before proceeding (non-blocking — delayed onboarding
    // is a legitimate case, so we warn rather than block).
    const emp = formData._employmentDate;
    const start = formData.startDate;
    // Both values are 'yyyy-MM-dd' strings (DateTimePicker output + the stashed
    // employment date), so compare by the date prefix — timezone-agnostic.
    const sameDay = (a, b) => String(a).slice(0, 10) === String(b).slice(0, 10);
    if (emp && start && !sameDay(start, emp)) {
      setStartDateDialog({ isOpen: true, formData });
      return false;
    }
    return doCreate(formData);
  }, [doCreate]);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    const requestPeriod = REQUEST_APPROVAL_PERIOD(row.status, row.currentPeriod);
    if (requestPeriod) {
      actions.push({
        type: 'edit',
        label: t('onboarding.action.requestApproval', { period: t(`onboarding.period.${requestPeriod}`) }),
        onClick: () => setRequestDialog({ isOpen: true, id: row.id, person: row.personFullName, period: requestPeriod, note: '' }),
      });
    }
    if (row.status === 'day90_pending_approval' || row.status === 'extended') {
      actions.push({
        type: 'details',
        label: t('onboarding.action.close'),
        onClick: () => setCloseDialog({ isOpen: true, id: row.id, person: row.personFullName, notes: '', reason: '', expectedDay90: row.expectedDay90 }),
      });
    }
    if (row.status === 'active' || row.status === 'day90_pending_approval') {
      actions.push({
        type: 'details',
        label: t('onboarding.action.extend'),
        onClick: () => setExtendDialog({ isOpen: true, id: row.id, person: row.personFullName, days: '', reason: '' }),
      });
    }
    if (row.status !== 'completed' && row.status !== 'cancelled') {
      actions.push({ type: 'divider' });
      actions.push({
        type: 'delete',
        label: t('onboarding.action.cancel'),
        onClick: () => setCancelDialog({ isOpen: true, id: row.id, person: row.personFullName, reason: '' }),
      });
    }
    return actions;
  }, [t, writable]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('onboarding.new')}
        schema={objectSchema}
        initialValues={null}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={startDateDialog.isOpen}
        title={t('onboarding.startDateMismatch.title')}
        message={t('onboarding.startDateMismatch.message')}
        onConfirm={async () => {
          const fd = startDateDialog.formData;
          setStartDateDialog({ isOpen: false, formData: null });
          await doCreate(fd);
        }}
        onCancel={() => setStartDateDialog({ isOpen: false, formData: null })}
      />

      <Modal $open={requestDialog.isOpen}>
        {requestDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboarding.action.requestApprovalTitle')}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 14 }}>
              {t('onboarding.requestApprovalConfirm', {
                person: requestDialog.person,
                period: t(`onboarding.period.${requestDialog.period}`),
              })}
            </p>
            <Field>
              <FieldLabel>{t('onboarding.requestApprovalNote')}</FieldLabel>
              <FieldText value={requestDialog.note}
                onChange={(e) => setRequestDialog({ ...requestDialog, note: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setRequestDialog({ isOpen: false, id: null, person: '', period: '', note: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy} onClick={async () => {
                setBusy(true);
                await requestApproval(requestDialog.id, requestDialog.period, requestDialog.note || undefined);
                setRequestDialog({ isOpen: false, id: null, person: '', period: '', note: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <Modal $open={extendDialog.isOpen}>
        {extendDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboarding.action.extend')} — {extendDialog.person}</h3>
            <Field>
              <FieldLabel>{t('onboarding.extensionDays')}</FieldLabel>
              <FieldInput type="number" min="1" max="60" value={extendDialog.days}
                onChange={(e) => setExtendDialog({ ...extendDialog, days: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel>{t('onboarding.extensionReason')}</FieldLabel>
              <FieldText value={extendDialog.reason}
                onChange={(e) => setExtendDialog({ ...extendDialog, reason: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setExtendDialog({ isOpen: false, id: null, person: '', days: '', reason: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy || !extendDialog.days} onClick={async () => {
                setBusy(true);
                await extend(extendDialog.id, { extensionDays: parseInt(extendDialog.days, 10), reason: extendDialog.reason || undefined });
                setExtendDialog({ isOpen: false, id: null, person: '', days: '', reason: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      <Modal $open={closeDialog.isOpen}>
        {closeDialog.isOpen && (() => {
          // Beklenen 90. günden önce kapatılıyorsa gerekçe ZORUNLU (backend 400 ile
          // otoriter — ob.onboarding.earlyCompletionReasonRequired). Gün bazında eşik.
          const early = isEarlyClose(closeDialog.expectedDay90);
          const resetClose = () => setCloseDialog({ isOpen: false, id: null, person: '', notes: '', reason: '', expectedDay90: null });
          return (
            <ModalCard>
              <h3 style={{ margin: '0 0 16px' }}>{t('onboarding.action.close')} — {closeDialog.person}</h3>
              <Field>
                <FieldLabel>{t('onboarding.handoverNotesToPerformance')}</FieldLabel>
                <FieldText value={closeDialog.notes}
                  onChange={(e) => setCloseDialog({ ...closeDialog, notes: e.target.value })} />
              </Field>
              {early && (
                <Field>
                  <FieldLabel>{t('onboarding.earlyCompletionReason')} *</FieldLabel>
                  <FieldText value={closeDialog.reason}
                    onChange={(e) => setCloseDialog({ ...closeDialog, reason: e.target.value })} />
                  <p style={{ fontSize: 12, color: '#B45309', margin: '6px 0 0' }}>{t('onboarding.earlyCloseHint')}</p>
                </Field>
              )}
              <p style={{ fontSize: 13, color: '#475569' }}>{t('onboarding.closeWarning')}</p>
              <Actions>
                <Button variant="outline" onClick={resetClose}>{t('common.cancel')}</Button>
                <Button disabled={busy || (early && !closeDialog.reason.trim())} onClick={async () => {
                  setBusy(true);
                  const ok = await close(closeDialog.id, {
                    handoverNotesToPerformance: closeDialog.notes || undefined,
                    earlyCompletionReason: early ? closeDialog.reason.trim() : undefined,
                  });
                  // Başarısız kapatmada diyalog açık kalır — kullanıcı girdiyi düzeltebilsin
                  if (ok) resetClose();
                  setBusy(false);
                }}>{busy ? '...' : t('common.confirm')}</Button>
              </Actions>
            </ModalCard>
          );
        })()}
      </Modal>

      <Modal $open={cancelDialog.isOpen}>
        {cancelDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboarding.action.cancel')} — {cancelDialog.person}</h3>
            <Field>
              <FieldLabel>{t('onboarding.cancelReason')}</FieldLabel>
              <FieldText value={cancelDialog.reason}
                onChange={(e) => setCancelDialog({ ...cancelDialog, reason: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setCancelDialog({ isOpen: false, id: null, person: '', reason: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy} onClick={async () => {
                setBusy(true);
                await cancel(cancelDialog.id, cancelDialog.reason || undefined);
                setCancelDialog({ isOpen: false, id: null, person: '', reason: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {useMemo(() => (
        <DataTable
          title={t('onboarding.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
